// Strings are German in code; English comes from the same tables as the Mac app (apple/l10n) plus the web's own
// (web/src/l10n/web.json). Keys use the Mac's placeholders: `%@` for text, `%lld` for counts (plural forms).
import appStrings from '../../../apple/l10n/app.json' with { type: 'json' };
import designStrings from '../../../apple/l10n/design.json' with { type: 'json' };
import webStrings from '../l10n/web.json' with { type: 'json' };

export type Lang = 'de' | 'en';
type Plural = Record<'de' | 'en', Record<string, string>>;
type Table = Record<string, string | Plural>;

const tables: Table[] = [webStrings as Table, appStrings as Table, designStrings as Table];

export type Arg = string | number;
export type Translate = (key: string, ...args: Arg[]) => string;

const missing = new Set<string>();

/** Keys looked up without an English text (tests and the dev console report them). */
export function missingKeys(): string[] {
  return [...missing].sort();
}

function lookup(key: string): string | Plural | undefined {
  for (const table of tables) {
    const value = table[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

export function hasKey(key: string): boolean {
  return lookup(key) !== undefined;
}

/** Fills `%@`, `%lld`, `%d` and positional `%1$@` in order; `%%` is a percent sign. */
function fill(template: string, args: readonly Arg[]): string {
  let next = 0;
  return template.replace(/%(?:(\d+)\$)?(@|lld|ld|d)|%%/g, (match, position: string | undefined) => {
    if (match === '%%') return '%';
    const index = position ? Number(position) - 1 : next++;
    const value = args[index];
    return value === undefined ? '' : String(value);
  });
}

export function translate(lang: Lang, key: string, args: readonly Arg[] = []): string {
  const entry = lookup(key);
  if (entry === undefined) {
    if (lang === 'en') missing.add(key);
    return fill(key, args);
  }
  if (typeof entry === 'string') return fill(lang === 'de' ? key : entry, args);
  const count = Number(args.find((a) => typeof a === 'number') ?? 0);
  const forms = entry[lang];
  const category = new Intl.PluralRules(lang).select(count);
  return fill(forms[category] ?? forms.other ?? key, args);
}

export function translator(lang: Lang): Translate {
  return (key, ...args) => translate(lang, key, args);
}

/** The browser's language, German unless it prefers English. */
export function preferredLang(languages: readonly string[] = typeof navigator === 'undefined' ? [] : navigator.languages): Lang {
  for (const l of languages) {
    if (l.toLowerCase().startsWith('de')) return 'de';
    if (l.toLowerCase().startsWith('en')) return 'en';
  }
  return 'de';
}
